import { useState, useEffect, useMemo, useRef } from 'react';
import { NFLWaiverBadge } from '@/components/nfl/NFLWaiverBadge';
import { isOnWaivers as playerIsOnWaivers, waiverAvailabilityLabel, type WaiverInfo } from '@/lib/nflWaiverBadge';
import { supabase } from '@/integrations/supabase/client';
import NFLSeasonLine from '@/components/nfl/NFLSeasonLine';
import { computeBreakdown, resolveTerms, type ModelTerm, type BreakdownResult } from '@/lib/nflScoringBreakdown';
import { useNFLLeague } from '@/contexts/NFLLeagueContext';
import { useNFLDemoOptional } from '@/contexts/NFLDemoContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  X, Heart, ShieldAlert, Activity, FileText, BarChart3, Newspaper,
  ChevronDown, ChevronUp, UserPlus, UserMinus, ArrowLeftRight, ArrowUpDown, CheckCircle, Lock, Clock,
} from 'lucide-react';
import { useNflWeekGames } from '@/hooks/useNflWeekGames';
import { gameLine } from '@/lib/nflGameLine';
import { useNflFranchiseSchedule } from '@/hooks/useNflFranchiseSchedule';
import { useWeeklyProjections, formatWeeklyProj } from '@/hooks/useWeeklyProjections';
import { buildGameLogWeeks, hotStreakKeys, type GameLogGame, type GameLogWeek } from '@/lib/nflGameLog';
import { cn } from '@/lib/utils';
import PlayerNewsList from '@/components/player/PlayerNewsList';
import { AvailabilityStrip } from '@/components/nfl/PlayerAvailability';
import type { RosterPlayer } from './NFLMyTeamPage';

/**
 * SAB-814 (mobile) — optional lineup-action bundle. Passed ONLY by NFLMyTeamPage when the modal
 * is opened for a player on the viewer's OWN team. The desktop RosterRow move/IR/drop buttons are
 * `hidden sm:flex`; this is the mobile surface for the same actions. It carries the roster player
 * plus the SAME shared handlers NFLMyTeamPage already uses (handleStartMove / handleMoveIr /
 * handleDrop) — no reimplementation, no duplicate lock/eligibility logic. Move/Swap starts
 * select-mode and closes the modal so the roster's (already responsive) tap-to-target flow takes
 * over. Absent for the modal's other four callers (free-agent browse, other teams, draft, matchup),
 * so their behaviour is unchanged.
 */
export interface NFLLineupActions {
  rosterPlayer: RosterPlayer;
  irEligible: boolean;
  selecting: boolean;
  busy: boolean;
  onStartMove: (p: RosterPlayer) => void;
  onMoveIr: (p: RosterPlayer) => void;
  onDrop: (p: RosterPlayer) => void;
}

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface PlayerInfo {
  id: string;
  full_name: string;
  position: string;
  nfl_franchise_id: string | null;
  jersey_number: string | null;
  height: number | null;
  weight: number | null;
  college: string | null;
  experience: number | null;
  draft_year: number | null;
  draft_round: number | null;
  draft_pick: number | null;
  injury_status: string | null;
  injury_body_part: string | null;
  headshot_url: string | null;
  status: string;
}

export interface GameStatRow {
  id: string;
  week: number;
  season_year: number;
  stats: Record<string, any>;
  trash_points: number;
  anti_points: number;
  sabotage_points: number;
  /** Scoring CONTEXT. Both are NULL on every row before SAB-825, which is why the
   *  breakdown blocks on historical weeks -- see the fallback in the game log. */
  team_lost: boolean | null;
  position: string | null;
  /** SAB-1127: was the game final when scored? NULL = pre-column, treated as final. */
  outcome_final: boolean | null;
  /** SAB-1219: the franchise he played FOR in this game. NULL until backfilled. */
  franchise_id?: string | null;
  /** SAB-1219: the played game itself, embedded, so a traded player's past opponent comes
   *  from the game he actually played rather than his current team's schedule. */
  game?: GameLogGame | null;
}

interface TransactionRow {
  id: string;
  transaction_type: string;
  team_id: string;
  from_team_id: string | null;
  to_team_id: string | null;
  details: Record<string, any> | null;
  created_at: string;
  team_name?: string;
  from_team_name?: string;
  to_team_name?: string;
}

interface NFLPlayerDetailModalProps {
  playerId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onNavigateToTrades?: () => void;
  // SAB-814 (mobile): present only when opened from the viewer's own roster. See NFLLineupActions.
  lineup?: NFLLineupActions;
  // SAB-1045: the server-derived waiver designation for THIS player, handed down from the
  // list row rather than re-fetched.
  //
  // Optional, and the six other call sites correctly do not pass it: a player on waivers
  // is by definition UNROSTERED, so My Team, the matchup detail and the draft room cannot
  // display one. The two Players lists are the only surfaces where the case arises, and
  // both already hold the row that carries it.
  //
  // Passed down rather than queried here so the chip and the list badge are the same read.
  // A second query could return a different answer across the drop lock's boundary and
  // show "W (Fri)" in the list beside no chip on the detail of the same player.
  waiver?: WaiverInfo;
  // SAB-1172: which tab to open on. The news badge opens the card straight at 'news';
  // every other caller omits it and gets 'stats' as before.
  initialTab?: Tab;
  /**
   * SAB-1045 follow-up: opens the SAME acquisition flow the list rows use.
   *
   * The modal does NOT decide add-vs-bid. It hands off to the caller's openAcquisition,
   * which routes on the server-derived waiver_state and lets nfl-add-drop's refusal have
   * the final word. A second decision here is exactly the drift SAB-1035 removed.
   *
   * Optional, and the Add button renders ONLY when it is supplied. A button that is
   * present but does nothing is worse than an absent one -- which is what this was: an
   * onClick containing a bare `// TODO`.
   */
  onAcquire?: () => void;
  /**
   * SAB-1062: opens the propose-trade flow pre-targeted at the team that OWNS this player,
   * with the player pre-added to their side.
   *
   * Optional, and the Trade button renders ONLY when it is supplied. The button previously
   * called `onNavigateToTrades?.()` -- optional chaining, so on every caller that did not
   * pass it the tap did nothing at all. Same inert-button defect as the Add button in
   * SAB-1045, and the same fix: a control that cannot act is not rendered.
   */
  onTrade?: (ownerTeamId: string, playerId: string) => void;
}

/* ------------------------------------------------------------------ */
/*  Position-specific stat keys                                        */
/* ------------------------------------------------------------------ */

/**
 * Counting-stat vocabulary, keyed to the SHAPE THE DATA ACTUALLY HAS.
 *
 * These entries used to be flat keys (`rush_att`) read as `stats[key]`. The column stores
 * SportRadar's payload verbatim, which is NESTED BY GROUP:
 *
 *   stats = { rushing: { attempts, yards, touchdowns, ... }, receiving: { ... } }
 *
 * so every lookup returned undefined and rendered `|| 0`. Not one of 38,904 rows carried a
 * single key this table asked for, in either season -- the game log had never displayed a
 * non-zero counting stat for any player, and nobody noticed until 2026 REG week 1 put real
 * numbers behind it. The header kept working throughout because trash_points / anti_points /
 * sabotage_points are flat TOP-LEVEL COLUMNS, read directly and never through `stats`.
 *
 * `path` is the dotted path into `stats`. Read it with statAt(), never with stats[key].
 *
 * Paths below were taken from the live payload (jsonb_object_keys over all rows), not from
 * the SportRadar docs and not from the old key names -- guessing at field names is the
 * mistake that produced this bug.
 *
 * DELIBERATELY ABSENT: DST 'td_allowed' and COACH 'challenge_success'. Neither exists
 * anywhere in the payload. They rendered 0 before this change and would render 0 after it,
 * so they are removed rather than mapped to a plausible-looking neighbour. If either is
 * wanted, it needs a real source first.
 */
type StatDef = { key: string; label: string; path: string };

const POSITION_STATS: Record<string, StatDef[]> = {
  QB: [
    { key: 'pass_att',      label: 'Pass Att', path: 'passing.attempts' },
    { key: 'pass_comp',     label: 'Comp',     path: 'passing.completions' },
    { key: 'pass_yds',      label: 'Pass Yds', path: 'passing.yards' },
    { key: 'pass_td',       label: 'Pass TD',  path: 'passing.touchdowns' },
    { key: 'interceptions', label: 'INT',      path: 'passing.interceptions' },
    { key: 'sacks_taken',   label: 'Sacks',    path: 'passing.sacks' },
    { key: 'fumbles',       label: 'Fum',      path: 'fumbles.fumbles' },
    { key: 'rush_att',      label: 'Rush Att', path: 'rushing.attempts' },
    { key: 'rush_yds',      label: 'Rush Yds', path: 'rushing.yards' },
    { key: 'rush_td',       label: 'Rush TD',  path: 'rushing.touchdowns' },
  ],
  RB: [
    { key: 'rush_att',   label: 'Rush Att', path: 'rushing.attempts' },
    { key: 'rush_yds',   label: 'Rush Yds', path: 'rushing.yards' },
    { key: 'rush_td',    label: 'Rush TD',  path: 'rushing.touchdowns' },
    { key: 'fumbles',    label: 'Fum',      path: 'fumbles.fumbles' },
    { key: 'targets',    label: 'Tgt',      path: 'receiving.targets' },
    { key: 'receptions', label: 'Rec',      path: 'receiving.receptions' },
    { key: 'rec_yds',    label: 'Rec Yds',  path: 'receiving.yards' },
    { key: 'rec_td',     label: 'Rec TD',   path: 'receiving.touchdowns' },
  ],
  WR: [
    { key: 'targets',    label: 'Tgt',      path: 'receiving.targets' },
    { key: 'receptions', label: 'Rec',      path: 'receiving.receptions' },
    { key: 'rec_yds',    label: 'Rec Yds',  path: 'receiving.yards' },
    { key: 'rec_td',     label: 'Rec TD',   path: 'receiving.touchdowns' },
    { key: 'drops',      label: 'Drops',    path: 'receiving.dropped_passes' },
    { key: 'rush_att',   label: 'Rush Att', path: 'rushing.attempts' },
    { key: 'rush_yds',   label: 'Rush Yds', path: 'rushing.yards' },
    { key: 'fumbles',    label: 'Fum',      path: 'fumbles.fumbles' },
  ],
  TE: [
    { key: 'targets',    label: 'Tgt',     path: 'receiving.targets' },
    { key: 'receptions', label: 'Rec',     path: 'receiving.receptions' },
    { key: 'rec_yds',    label: 'Rec Yds', path: 'receiving.yards' },
    { key: 'rec_td',     label: 'Rec TD',  path: 'receiving.touchdowns' },
    { key: 'drops',      label: 'Drops',   path: 'receiving.dropped_passes' },
  ],
  K: [
    { key: 'fg_made',   label: 'FG Made', path: 'field_goals.made' },
    { key: 'fg_att',    label: 'FG Att',  path: 'field_goals.attempts' },
    { key: 'fg_missed', label: 'FG Miss', path: 'field_goals.missed' },
    { key: 'xp_made',   label: 'XP Made', path: 'extra_points.made' },
    { key: 'xp_att',    label: 'XP Att',  path: 'extra_points.attempts' },
    { key: 'xp_missed', label: 'XP Miss', path: 'extra_points.missed' },
  ],
  DST: [
    { key: 'sacks',         label: 'Sacks',     path: 'dst.sacks' },
    { key: 'interceptions', label: 'INTs',      path: 'dst.interceptions' },
    { key: 'fumble_rec',    label: 'Fum Rec',   path: 'dst.fumble_recoveries' },
    { key: 'safeties',      label: 'Safeties',  path: 'dst.safeties' },
    { key: 'pts_allowed',   label: 'Pts Allow', path: 'dst.points_allowed' },
    { key: 'yds_allowed',   label: 'Yds Allow', path: 'dst.yards_allowed' },
  ],
  COACH: [
    // team_won / team_lost are BOOLEANS per game; Number(true)=1 sums to a W/L count.
    { key: 'wins',      label: 'Wins',    path: 'coach.team_won' },
    { key: 'losses',    label: 'Losses',  path: 'coach.team_lost' },
    { key: 'penalties', label: 'Pen',     path: 'coach.team_penalties' },
    { key: 'own_score', label: 'Pts For', path: 'coach.own_score' },
  ],
  P: [
    { key: 'punts',           label: 'Punts',    path: 'punts.attempts' },
    { key: 'punt_yds',        label: 'Punt Yds', path: 'punts.yards' },
    { key: 'punt_avg',        label: 'Avg',      path: 'punts.avg_yards' },
    { key: 'punt_inside_20',  label: 'In 20',    path: 'punts.inside_20' },
  ],
};

// IDP positions share the same stat layout
const IDP_STATS: StatDef[] = [
  { key: 'tackles',          label: 'Tkl',   path: 'defense.tackles' },
  { key: 'assists',          label: 'Ast',   path: 'defense.assists' },
  { key: 'sacks',            label: 'Sacks', path: 'defense.sacks' },
  { key: 'interceptions',    label: 'INTs',  path: 'defense.interceptions' },
  { key: 'pass_deflections', label: 'PD',    path: 'defense.passes_defended' },
  { key: 'forced_fumbles',   label: 'FF',    path: 'defense.forced_fumbles' },
];
for (const pos of ['DB', 'LB', 'DL', 'IDP']) POSITION_STATS[pos] = IDP_STATS;

/**
 * Read a dotted path out of the stats payload as a number.
 *
 * Returns 0 for a missing group, a missing field, or a non-numeric leaf. Booleans coerce
 * (team_won -> 1/0), which is what the COACH rows want. A group that exists but is an
 * OBJECT (e.g. `stats.fumbles`) yields 0 rather than NaN -- that was the old failure for
 * every field, and it must not silently return NaN into a .toFixed().
 */
export function statAt(stats: unknown, path: string): number {
  let cur: unknown = stats;
  for (const seg of path.split('.')) {
    if (cur === null || typeof cur !== 'object') return 0;
    cur = (cur as Record<string, unknown>)[seg];
  }
  if (typeof cur === 'boolean') return cur ? 1 : 0;
  const n = Number(cur);
  return Number.isFinite(n) ? n : 0;
}

function getStatsForPosition(pos: string) {
  return POSITION_STATS[pos] || POSITION_STATS.WR; // fallback
}

/* ------------------------------------------------------------------ */
/*  Position badge colors (shared with roster)                         */
/* ------------------------------------------------------------------ */

const positionColors: Record<string, string> = {
  QB: 'bg-red-500/20 text-red-400 border-red-500/30',
  RB: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  WR: 'bg-sky-500/20 text-sky-400 border-sky-500/30',
  TE: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  K: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
  P: 'bg-teal-500/20 text-teal-400 border-teal-500/30',
  DST: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  COACH: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
  DB: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
  LB: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
  DL: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
  IDP: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
};

const injuryColors: Record<string, string> = {
  Out: 'text-red-400 bg-red-400/10 border-red-400/20',
  IR: 'text-red-400 bg-red-400/10 border-red-400/20',
  'Injured Reserve': 'text-red-400 bg-red-400/10 border-red-400/20',
  Suspended: 'text-red-400 bg-red-400/10 border-red-400/20',
  Doubtful: 'text-orange-400 bg-orange-400/10 border-orange-400/20',
  Questionable: 'text-amber-400 bg-amber-400/10 border-amber-400/20',
  Probable: 'text-green-400 bg-green-400/10 border-green-400/20',
  PUP: 'text-purple-400 bg-purple-400/10 border-purple-400/20',
};

const IR_ELIGIBLE = ['Out', 'IR', 'Injured Reserve', 'Doubtful', 'Suspended', 'PUP'];

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

type Tab = 'stats' | 'gamelog' | 'news' | 'injury' | 'activity';

export default function NFLPlayerDetailModal({
  playerId, open, onOpenChange, onNavigateToTrades, lineup, onTrade,
  waiver, onAcquire, initialTab,
}: NFLPlayerDetailModalProps) {
  const { currentLeague, currentTeam, season, currentWeek } = useNFLLeague();
  const demoCtx = useNFLDemoOptional();
  const isDemoMode = demoCtx?.isDemoMode ?? false;
  const guardWrite = demoCtx?.guardWrite ?? (() => false);
  // This week's game for the header. Current week only: the game log's per-game opponent
  // needs the player's franchise AT that game, which nothing stores yet (SAB-1219).
  const weekGames = useNflWeekGames(currentWeek, season, open && !isDemoMode);

  // SAB-1176: Game Log is the default (was Stats). The breakdown is the thing that makes an
  // inverted score trustworthy -- a QB's 22 pass attempts being worth +22 while his 143
  // passing yards are -4.3 is not guessable -- so the card opens on the tab that explains
  // the number rather than on the one that restates it.
  const [tab, setTab] = useState<Tab>(initialTab ?? 'gamelog');

  // SAB-1172: re-apply on each open. Without this the modal keeps whatever tab the
  // previous visit left behind, so the news badge would open on 'stats' the second
  // time it is used in a session.
  useEffect(() => {
    if (open) setTab(initialTab ?? 'gamelog');
  }, [open, playerId, initialTab]);
  const [player, setPlayer] = useState<PlayerInfo | null>(null);
  const [teamAbbr, setTeamAbbr] = useState<string | null>(null);
  const [gameStats, setGameStats] = useState<GameStatRow[]>([]);
  // Model weights + this league's overrides, exactly as NFLMatchupDetail loads them. The
  // panel is driven off these rather than a hand-kept stat list, so it can only ever show
  // what actually scored: a stat with no weight produces no contribution and cannot appear.
  const [terms, setTerms] = useState<ModelTerm[] | null>(null);
  const [transactions, setTransactions] = useState<TransactionRow[]>([]);
  const [ownerTeamName, setOwnerTeamName] = useState<string | null>(null);
  // SAB-1062: the owning team's id. The name was already here; the id is what a trade
  // needs, and not keeping it is why the Trade button had nothing to target.
  const [ownerTeamId, setOwnerTeamId] = useState<string | null>(null);
  const [isOnMyTeam, setIsOnMyTeam] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [statMode, setStatMode] = useState<'totals' | 'per_game'>('totals');
  // SAB-1219: keyed by ROW, not week -- a traded player can have two games in one week.
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  // Lock body scroll while modal is open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Fetch all data when modal opens
  useEffect(() => {
    if (!open || !playerId) {
      setPlayer(null);
      setGameStats([]);
      setTransactions([]);
      setTab('stats');
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    async function load() {
      // Parallel fetches
      const [playerRes, statsRes, txRes, ownerRes] = await Promise.all([
        // 1. Player profile
        supabase
          .from('nfl_players')
          .select('id, full_name, position, nfl_franchise_id, jersey_number, height, weight, college, experience, draft_year, draft_round, draft_pick, injury_status, injury_body_part, headshot_url, status')
          .eq('id', playerId)
          .single(),

        // 2. Game stats (regular-season weeks of the league's own season)
        //
        // SAB-824: was `.eq('season_year', 2025)` -- a bare literal, the last one in the
        // client. A 2026 league rendered last season's game log: never empty, never an
        // error, because 2025 REG is a complete 18-week season with 18,985 stat rows.
        // Season now comes from the context (`season`), which is the league's own.
        //
        // season_type pinned to REG for consistency with every other NFL surface (locks,
        // byes, the sabotage store). This is a deliberate SCOPE choice, not a lookup:
        // 2026 currently holds ONLY preseason stats, so a 2026 league sees an empty game
        // log until REG week 1 lands on 2026-09-10. Correct but thin -- SAB-902 tracks
        // showing all phases with a per-row phase column, and is the one site with a real
        // claim on the SAB-901 current-phase resolver.
        //
        // `supabase as any`: third chained .eq() trips TS2589, same escape hatch as
        // NFLPlayersPage. Row shape asserted at the consumption site.
        // Gated on `season`: with no league selected there is no season, and the right
        // behaviour is to SKIP the query rather than pass null into it. `.eq(col, null)`
        // would return an empty set that looks identical to "this player never played" --
        // the same silent-plausible-answer shape this ticket exists to remove.
        season !== null
          ? (supabase as any)
              .from('nfl_player_game_stats')
              // SAB-1219: + franchise_id and the played game (via the game_id FK), which is
              // what places a played week and names its opponent in the full-season log.
              .select('id, week, season_year, stats, trash_points, anti_points, sabotage_points, team_lost, position, outcome_final, franchise_id, game:nfl_games(id, week, home_franchise_id, away_franchise_id, scheduled, status)')
              .eq('player_id', playerId)
              .eq('season_year', season)
              .eq('season_type', 'REG')
              .order('week', { ascending: false })
          : Promise.resolve({ data: null, error: null }),

        // 3. Transactions
        currentLeague
          ? supabase
              .from('nfl_roster_transactions')
              .select('id, transaction_type, team_id, from_team_id, to_team_id, details, created_at')
              .eq('player_id', playerId)
              .eq('league_id', currentLeague.id)
              .order('created_at', { ascending: false })
              .limit(20)
          : Promise.resolve({ data: null, error: null }),

        // 4. Current owner
        currentLeague
          ? supabase
              .from('nfl_rosters')
              .select('team_id, nfl_teams(id, name)')
              .eq('player_id', playerId)
              .eq('league_id', currentLeague.id)
              .eq('is_active', true)
              .limit(1)
          : Promise.resolve({ data: null, error: null }),
      ]);

      if (cancelled) return;

      const p = playerRes.data as any;
      if (p) {
        setPlayer(p);
        // Fetch franchise abbreviation
        if (p.nfl_franchise_id) {
          const { data: franchise } = await supabase
            .from('nfl_franchises')
            .select('alias')
            .eq('id', p.nfl_franchise_id)
            .single();
          if (!cancelled) setTeamAbbr((franchise as any)?.alias || null);
        }
      }

      setGameStats((statsRes.data || []) as GameStatRow[]);

      // Resolve team names for transactions
      if (txRes.data && txRes.data.length > 0) {
        const teamIds = new Set<string>();
        for (const tx of txRes.data) {
          if (tx.team_id) teamIds.add(tx.team_id);
          if (tx.from_team_id) teamIds.add(tx.from_team_id);
          if (tx.to_team_id) teamIds.add(tx.to_team_id);
        }
        const { data: teamNames } = await supabase
          .from('nfl_teams')
          .select('id, name')
          .in('id', [...teamIds]);

        const nameMap: Record<string, string> = {};
        for (const t of (teamNames || [])) nameMap[t.id] = t.name;

        if (!cancelled) {
          setTransactions(txRes.data.map(tx => ({
            ...tx,
            team_name: nameMap[tx.team_id] || 'Unknown',
            from_team_name: tx.from_team_id ? (nameMap[tx.from_team_id] || 'Unknown') : undefined,
            to_team_name: tx.to_team_id ? (nameMap[tx.to_team_id] || 'Unknown') : undefined,
          })) as TransactionRow[]);
        }
      } else {
        setTransactions([]);
      }

      // Owner info
      const ownerRow = (ownerRes.data as any)?.[0];
      if (ownerRow) {
        const teamName = (ownerRow.nfl_teams as any)?.name || null;
        if (!cancelled) {
          setOwnerTeamName(teamName);
          setOwnerTeamId(ownerRow.team_id ?? null);
          setIsOnMyTeam(ownerRow.team_id === currentTeam?.id);
        }
      } else {
        if (!cancelled) {
          setOwnerTeamName(null);
          setOwnerTeamId(null);
          setIsOnMyTeam(false);
        }
      }

      if (!cancelled) setIsLoading(false);
    }

    load();
    return () => { cancelled = true; };
  // SAB-824: `season` is a dependency -- switching leagues changes the season the game
  // log is keyed on, and without it the modal would keep showing the previous league's.
  }, [playerId, open, currentLeague, currentTeam, season]);

  // Computed season totals
  const seasonTotals = useMemo(() => {
    if (gameStats.length === 0) return { trash: 0, anti: 0, sabotage: 0, gp: 0 };
    let trash = 0, anti = 0, sabotage = 0;
    for (const g of gameStats) {
      trash += Number(g.trash_points) || 0;
      anti += Number(g.anti_points) || 0;
      sabotage += Number(g.sabotage_points) || 0;
    }
    return { trash, anti, sabotage, gp: gameStats.length };
  }, [gameStats]);

  // Aggregated stats (totals + per game)
  const aggregatedStats = useMemo(() => {
    if (!player || gameStats.length === 0) return [];
    const statKeys = getStatsForPosition(player.position);
    const totals: Record<string, number> = {};

    for (const g of gameStats) {
      for (const { key, path } of statKeys) {
        totals[key] = (totals[key] || 0) + statAt(g.stats, path);
      }
    }

    return statKeys.map(({ key, label }) => ({
      key,
      label,
      total: totals[key] || 0,
      perGame: gameStats.length > 0 ? (totals[key] || 0) / gameStats.length : 0,
    }));
  }, [player, gameStats]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      supabase.from('nfl_scoring_model' as any).select('weights').eq('is_active', true).maybeSingle(),
      currentLeague
        ? supabase.from('nfl_scoring_settings' as any).select('weights').eq('league_id', currentLeague.id).maybeSingle()
        : Promise.resolve({ data: null } as any),
    ]).then(([modelRes, settingsRes]: any[]) => {
      if (cancelled) return;
      const mw = modelRes?.data?.weights;
      if (!Array.isArray(mw) || mw.length === 0) return;
      setTerms(resolveTerms(mw as ModelTerm[], settingsRes?.data?.weights));
    });
    return () => { cancelled = true; };
  }, [currentLeague]);

  // SAB-1219: the full season, 1..last, from the CURRENT franchise's schedule with this
  // player's stat rows joined in (lib/nflGameLog.ts has the row kinds and the opponent rule).
  // GP and the season totals above stay counted from stat rows only.
  const schedule = useNflFranchiseSchedule(player?.nfl_franchise_id, season, open && !isDemoMode);
  const logWeeks = useMemo(() => buildGameLogWeeks({
    stats: gameStats,
    schedule: schedule.data?.games ?? null,
    lastWeek: schedule.data?.lastWeek ?? null,
    currentFranchiseId: player?.nfl_franchise_id ?? null,
    aliasById: schedule.data?.aliasById ?? {},
  }), [gameStats, schedule.data, player?.nfl_franchise_id]);
  // 🔥: the current run of 3+ consecutive played weeks with TRS > 5 (see hotStreakKeys).
  const hotKeys = useMemo(() => hotStreakKeys(logWeeks), [logWeeks]);
  // Projection on the CURRENT week's row only: v_nfl_weekly_projections is season/17, the
  // same every non-bye week, so repeating it down every future row would be noise.
  const projMap = useWeeklyProjections(playerId && !isDemoMode ? [playerId] : [], currentWeek);
  const currentWeekProj = playerId ? projMap.get(playerId) : undefined;

  const isFreeAgent = !ownerTeamName;
  // SAB-1045 follow-up: ONE source for both the corner label and the button's wording.
  const onWaivers = playerIsOnWaivers(waiver);
  const waiverAvailability = waiverAvailabilityLabel(waiver);

  if (!open || !playerId) return null;

  const close = () => onOpenChange(false);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center"
      onClick={close}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

      {/* Modal */}
      <div
        className={cn(
          'relative z-[70] w-full sm:max-w-[600px] bg-card border border-border rounded-t-2xl sm:rounded-2xl',
          'max-h-[92vh] sm:max-h-[85vh] flex flex-col overflow-hidden',
          'animate-in slide-in-from-bottom sm:fade-in sm:zoom-in-95 duration-200'
        )}
        onClick={e => e.stopPropagation()}
      >
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : player ? (
          <>
            {/*
              SAB-813: the designation, full-width across the top of the sheet, before any
              other header content. Matching the reference: a red "+ IR" or a gold
              "? QUESTIONABLE" band you cannot scroll past.

              It replaces a small Badge that sat in the meta row between the team alias and
              the jersey number, keyed on `injury_status` alone. When SAB-813 was written
              that badge had never rendered, because injury_status was NULL for every
              player pre-season, so a player on IR opened a sheet saying nothing was wrong.

              CORRECTED 2026-09-14: injury_status IS populated now — 56 players on prod
              carry Questionable / Doubtful / Out, refreshed twice daily by
              sync-nfl-player-status. The original note read as "this code path is dead",
              which would stop the next person touching it. The strip is still the right
              component because it ALSO reads roster state (IR/PUP/SUS), which
              injury_status alone never covers.
            */}
            <AvailabilityStrip injuryStatus={player.injury_status} status={player.status} />

            {/* ── HEADER ── */}
            <div className="p-5 pb-3 border-b border-border/50">
              <button
                onClick={close}
                className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors z-20"
              >
                <X className="h-5 w-5" />
              </button>

              <div className="flex items-start gap-4">
                {/* Headshot */}
                <div className="h-20 w-20 rounded-xl bg-muted/50 overflow-hidden shrink-0 border border-border/40">
                  {player.headshot_url ? (
                    <img src={player.headshot_url} alt={player.full_name} className="h-full w-full object-cover" />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center text-xl text-muted-foreground font-bold">
                      {player.position}
                    </div>
                  )}
                </div>

                {/* Name + meta */}
                <div className="flex-1 min-w-0 pt-0.5">
                  <h2 className="text-lg font-display font-bold text-foreground truncate pr-8">
                    {player.full_name}
                  </h2>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <Badge className={cn(
                      'text-[10px] font-bold border px-1.5',
                      positionColors[player.position] || 'bg-muted/20 text-muted-foreground border-border'
                    )}>
                      {player.position}
                    </Badge>
                    {teamAbbr && (
                      <span className="text-xs text-muted-foreground font-medium">{teamAbbr}</span>
                    )}
                    {/* This week: a BYE chip, or the game line ("Sun 1:00 PM vs CIN") in the
                        same wording as My Team and the Players list. Keyed on the player's
                        CURRENT franchise, which is right for this week by definition. */}
                    {weekGames.isBye(player.nfl_franchise_id) ? (
                      <span className="text-[10px] font-bold text-amber-400 bg-amber-400/10 px-1 rounded">BYE</span>
                    ) : gameLine(weekGames.gameFor(player.nfl_franchise_id)) ? (
                      <span data-testid="player-card-game-line" className="flex items-center gap-1 text-xs text-muted-foreground whitespace-nowrap">
                        <Clock className="h-3 w-3" />
                        {gameLine(weekGames.gameFor(player.nfl_franchise_id))}
                      </span>
                    ) : null}
                    {player.jersey_number && (
                      <span className="text-xs text-muted-foreground">#{player.jersey_number}</span>
                    )}
                    {/* SAB-1045: sits in the meta row, which is already flex-wrap, so the
                        chip wraps instead of squeezing the position badge at 360px. */}
                    {waiver && <NFLWaiverBadge player={waiver} variant="chip" />}
                  </div>

                  {/* SAB PTS + Owner */}
                  <div className="flex items-center gap-3 mt-2">
                    <span className="text-xl font-bold text-primary tabular-nums">
                      {seasonTotals.sabotage >= 0 ? '+' : ''}{seasonTotals.sabotage.toFixed(1)}
                    </span>
                    <span className="text-[10px] text-muted-foreground uppercase tracking-wider">
                      SAB PTS · {seasonTotals.gp} GP
                    </span>
                    {/* SAB-1176: the three TRASH / ANTI / GAMES tiles are gone -- they competed
                        with the score and pushed the stat content below the fold. TRS and ANT
                        are pills beside the score instead, and GP rides in the eyebrow. */}
                    <span className="rounded-full border border-damage/30 bg-damage/[0.12] px-2 py-0.5 text-[11px] font-bold tabular-nums text-damage">
                      TRS +{seasonTotals.trash.toFixed(1)}
                    </span>
                    <span className="rounded-full border border-success/30 bg-success/[0.12] px-2 py-0.5 text-[11px] font-bold tabular-nums text-success">
                      ANT {seasonTotals.anti.toFixed(1)}
                    </span>
                    {ownerTeamName && (
                      <span className="text-[10px] text-muted-foreground ml-auto">
                        {isOnMyTeam ? 'My Team' : ownerTeamName}
                      </span>
                    )}
                    {isFreeAgent && (
                      // SAB-1045 follow-up: "Free Agent" was derived from ownership alone,
                      // so a player on waivers said "Free Agent" here while the chip beside
                      // their name said "W (Fri)". Same card, two answers, and the wrong one
                      // invited an add the server refuses. Both now read waiver_state.
                      waiverAvailability
                        ? <span className="text-[10px] text-amber-400 font-medium ml-auto whitespace-nowrap">{waiverAvailability}</span>
                        : <span className="text-[10px] text-emerald-400 font-medium ml-auto">Free Agent</span>
                    )}
                  </div>
                </div>
              </div>

            </div>

            {/* ── TABS ── */}
            <div className="flex border-b border-border/50 px-2 overflow-x-auto scrollbar-none">
              {([
                { id: 'gamelog' as Tab, label: 'Game Log', icon: FileText },
                { id: 'stats' as Tab, label: 'Stats', icon: BarChart3 },
                { id: 'news' as Tab, label: 'News', icon: Newspaper },
                { id: 'injury' as Tab, label: 'Injury', icon: ShieldAlert },
                { id: 'activity' as Tab, label: 'Activity', icon: Activity },
              ]).map(t => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-2.5 text-xs font-semibold whitespace-nowrap border-b-2 transition-colors',
                    tab === t.id
                      ? 'text-primary border-primary'
                      : 'text-muted-foreground border-transparent hover:text-foreground'
                  )}
                >
                  <t.icon className="h-3.5 w-3.5" />
                  {t.label}
                </button>
              ))}
            </div>

            {/* ── TAB CONTENT ── */}
            <div className="flex-1 overflow-y-auto min-h-0">
              {tab === 'stats' && (
                <>
                  {/* SAB-987: last season, above the current-season content. A drafter is
                      choosing on last year's production, and the current season is empty
                      until week 1 -- an empty panel was the whole complaint. Labelled with
                      its own year so it can never be read as now (the SAB-824 distinction). */}
                  <NFLSeasonLine playerId={playerId} position={player?.position ?? ''} className="mb-3" />
                  <StatsTab
                  stats={aggregatedStats}
                  statMode={statMode}
                  onModeChange={setStatMode}
                  gamesPlayed={seasonTotals.gp}
                  seasonYear={season}
                  />
                </>
              )}
              {tab === 'gamelog' && (
                <GameLogTab
                  weeks={logWeeks}
                  position={player.position}
                  expandedKey={expandedKey}
                  onToggle={k => setExpandedKey(expandedKey === k ? null : k)}
                  hotKeys={hotKeys}
                  terms={terms}
                  currentWeek={currentWeek}
                  currentWeekProj={currentWeekProj}
                />
              )}
              {/* SAB-1172: SportRadar transactions + injury designations, the same
                  PlayerNewsList MLB renders. Distinct from the two neighbouring tabs:
                  Injury shows the CURRENT designation off nfl_players, Activity shows
                  FANTASY roster moves from nfl_roster_transactions. This is real NFL
                  news, with history. */}
              {tab === 'news' && (
                <div className="p-3">
                  <PlayerNewsList sport="nfl" playerId={player.id} playerName={player.full_name} />
                </div>
              )}
              {tab === 'injury' && (
                <InjuryTab player={player} />
              )}
              {tab === 'activity' && (
                <ActivityTab transactions={transactions} />
              )}
            </div>

            {/* ── FOOTER ACTIONS ── */}
            {currentTeam && (
              <div className="border-t border-border/50 p-3 flex items-center gap-2">
                {/* SAB-814 (mobile): own-roster lineup actions. Delegates to the SAME shared
                    handlers NFLMyTeamPage uses (no reimplementation). Move/Swap starts select-mode
                    and closes so the roster's tap-to-target flow (already responsive) takes over. */}
                {lineup ? (
                  lineup.rosterPlayer.isLocked ? (
                    <span className="flex items-center gap-1 text-[11px] text-yellow-500 font-medium">
                      <Lock className="h-3.5 w-3.5" /> Locked — game started
                    </span>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1.5 text-xs"
                        disabled={lineup.busy}
                        onClick={() => { lineup.onStartMove(lineup.rosterPlayer); close(); }}
                      >
                        <ArrowUpDown className="h-3.5 w-3.5" /> Move
                      </Button>
                      {lineup.irEligible && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1.5 text-xs border-amber-500/40 text-amber-400 hover:text-amber-300"
                          disabled={lineup.busy}
                          onClick={() => { lineup.onMoveIr(lineup.rosterPlayer); close(); }}
                          title={lineup.rosterPlayer.irAuthority === 'rotowire_recent'
                            ? 'Move to IR — based on a RotoWire injury report, not an official designation'
                            : 'Move to IR'}
                        >
                          {/* SAB-1215: a news-authorised placement is labelled, never passed off as official. */}
                          {lineup.rosterPlayer.irAuthority === 'rotowire_recent' ? 'IR (reported injury)' : 'IR'}
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="destructive"
                        className="gap-1.5 text-xs"
                        disabled={lineup.busy}
                        onClick={() => { lineup.onDrop(lineup.rosterPlayer); close(); }}
                      >
                        <UserMinus className="h-3.5 w-3.5" /> Drop
                      </Button>
                    </>
                  )
                ) : (
                <>
                {isOnMyTeam && (
                  <Button
                    size="sm"
                    variant="destructive"
                    className="gap-1.5 text-xs"
                    onClick={() => {
                      if (isDemoMode) { guardWrite(); return; }
                      // TODO: wire to nfl-add-drop drop action (non-roster contexts; SAB-814 covers own roster)
                    }}
                  >
                    <UserMinus className="h-3.5 w-3.5" /> Drop
                  </Button>
                )}
                {isFreeAgent && onAcquire && (
                  <Button
                    size="sm"
                    className={cn('gap-1.5 text-xs',
                      // A waivered player is a CLAIM, not an add. Labelling it "Add" and
                      // then opening bid mode is the same contradiction one control down.
                      onWaivers
                        ? 'bg-amber-600 hover:bg-amber-500'
                        : 'bg-emerald-600 hover:bg-emerald-500')}
                    onClick={() => {
                      if (isDemoMode) { guardWrite(); return; }
                      // Close first: the acquisition UI is a modal too, and stacking it
                      // behind this one leaves the user tapping an invisible sheet.
                      close();
                      onAcquire();
                    }}
                  >
                    <UserPlus className="h-3.5 w-3.5" /> {onWaivers ? 'Place Claim' : 'Add'}
                  </Button>
                )}
                {/* SAB-1062: an OPPONENT's player only. On your own the button is absent,
                    matching the standings overlay (SAB-1046) -- you cannot trade with
                    yourself, and a greyed control invites a tap and explains nothing.
                    `onTrade && ownerTeamId` because a button with no handler or no target
                    is the inert control this ticket exists to remove. */}
                {!isFreeAgent && !isOnMyTeam && onTrade && ownerTeamId && (
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid="detail-trade"
                    className="gap-1.5 text-xs"
                    onClick={() => {
                      if (isDemoMode) { guardWrite(); return; }
                      // Close first: the propose flow is a sheet/dialog of its own and
                      // stacking it behind this one leaves the user tapping an invisible
                      // surface (the same ordering SAB-1045's Add button needed).
                      close();
                      onTrade(ownerTeamId, playerId!);
                    }}
                  >
                    <ArrowLeftRight className="h-3.5 w-3.5" /> Trade
                  </Button>
                )}
                </>
                )}
                <div className="flex-1" />
                {/* College + years in the league. Both are real SportRadar roster metadata,
                    but each read as a bug: `Yr 0` means ROOKIE (zero completed seasons, true
                    of 642 active players) and looked like a null, and a missing college left
                    the separator stranded as a lone dot. Built from parts so the separator
                    only appears when there is something on both sides of it. */}
                <span className="text-[10px] text-muted-foreground">
                  {[
                    player.college || null,
                    player.experience == null
                      ? null
                      : player.experience === 0
                        ? 'Rookie'
                        : `Yr ${player.experience}`,
                  ].filter(Boolean).join(' · ')}
                </span>
              </div>
            )}
          </>
        ) : (
          <div className="flex items-center justify-center py-20">
            <p className="text-sm text-muted-foreground">Player not found</p>
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Stats Tab                                                          */
/* ------------------------------------------------------------------ */

function StatsTab({
  stats, statMode, onModeChange, gamesPlayed, seasonYear,
}: {
  stats: { key: string; label: string; total: number; perGame: number }[];
  statMode: 'totals' | 'per_game';
  onModeChange: (m: 'totals' | 'per_game') => void;
  gamesPlayed: number;
  /** SAB-1123: this block aggregates the LEAGUE's own season, and sits directly beneath
   *  NFLSeasonLine, which renders LAST season and labels itself "<year> season". Two stacked
   *  totals blocks where only one carries a year is read as one continuous set of numbers --
   *  the SAB-824 "unlabelled and mistaken for now" shape, inverted. */
  seasonYear: number | null;
}) {
  if (stats.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <BarChart3 className="h-8 w-8 text-muted-foreground mb-3" />
        <p className="text-sm text-muted-foreground">No stats available</p>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-3">
      {/* Totals / Per Game toggle */}
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">
          {seasonYear ? `${seasonYear} ` : ''}Season Stats ({gamesPlayed} GP)
        </span>
        <div className="flex rounded-lg border border-border overflow-hidden">
          {(['totals', 'per_game'] as const).map(m => (
            <button
              key={m}
              onClick={() => onModeChange(m)}
              className={cn(
                'px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider transition-colors',
                statMode === m
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {m === 'totals' ? 'Total' : '/Game'}
            </button>
          ))}
        </div>
      </div>

      {/* Stat grid */}
      <div className="grid grid-cols-2 gap-2">
        {stats.map(s => (
          <div key={s.key} className="bg-muted/20 rounded-lg px-3 py-2 flex items-center justify-between">
            <span className="text-xs text-muted-foreground">{s.label}</span>
            <span className="text-sm font-bold text-foreground tabular-nums">
              {statMode === 'totals'
                ? Number.isInteger(s.total) ? s.total : s.total.toFixed(1)
                : s.perGame.toFixed(1)
              }
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Game Log Tab                                                       */
/* ------------------------------------------------------------------ */

// Exported for NFLPlayerDetailModal.gamelog.test.tsx. The blocked branch is the COMMON
// case (100% of every season before 2026 REG) and it is invisible in code review: it turns
// on computeBreakdown's return, not on anything readable in this file. It is asserted
// directly against this component rather than mocked.
export function GameLogTab({
  weeks, position, expandedKey, onToggle, hotKeys, terms, currentWeek, currentWeekProj,
}: {
  /** SAB-1219: the full season, one entry per week (lib/nflGameLog.ts buildGameLogWeeks). */
  weeks: GameLogWeek<GameStatRow>[];
  position: string;
  expandedKey: string | null;
  onToggle: (key: string) => void;
  /** Rows carrying the 🔥 marker (lib/nflGameLog.ts hotStreakKeys). */
  hotKeys: Set<string>;
  /** Model weights + league overrides. NULL until loaded, which makes every row take the
   *  raw-stats fallback rather than rendering an empty panel while the fetch is in flight. */
  terms: ModelTerm[] | null;
  /** The league's current week: scrolled into view on open, and the only row with a projection. */
  currentWeek?: number | null;
  /** This player's projection for currentWeek; undefined = none loaded. */
  currentWeekProj?: number | null;
}) {
  const statKeys = getStatsForPosition(position);

  // Open on the current week, not on week 1: 18 rows is more than the sheet shows, and the
  // week a manager is deciding on is the one that matters. Re-runs when that row first
  // appears (the schedule can land after the stat rows).
  const currentKey = weeks.find(w => w.week === currentWeek)?.key ?? null;
  const currentRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    currentRef.current?.scrollIntoView?.({ block: 'center' });
  }, [currentKey]);

  if (weeks.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <FileText className="h-8 w-8 text-muted-foreground mb-3" />
        <p className="text-sm text-muted-foreground">No games played</p>
      </div>
    );
  }

  const dash = <span className="text-xs font-bold tabular-nums text-muted-foreground/60">–</span>;

  return (
    <div className="divide-y divide-border/50">
      {weeks.map((w) => {
        const isCurrent = currentWeek != null && w.week === currentWeek;
        const ref = w.key === currentKey ? currentRef : undefined;
        const opponent = w.kind === 'bye' || !w.opponentAlias || w.isHome === null
          ? null
          : `${w.isHome ? 'vs' : '@'} ${w.opponentAlias}`;

        // The opponent STACKS under the week rather than taking its own column. MEASURED at
        // 390: as a separate 63px column it pushed the played row's TRS/ANT labels onto a
        // second line and the chevron past the sheet's right edge.
        const weekCell = (
          <div className="w-12 shrink-0 leading-tight">
            <span className="block text-xs font-bold text-muted-foreground">Wk {w.week}</span>
            <span data-testid="gamelog-opponent" className="block truncate text-[11px] text-muted-foreground">
              {w.kind === 'bye'
                ? <span className="rounded bg-amber-400/10 px-1 text-[10px] font-bold text-amber-400">BYE</span>
                // Unknown opponent (a played game whose franchise is not yet backfilled and
                // was not the current team's) reads as unknown, not as a guess.
                : opponent ?? '—'}
            </span>
          </div>
        );

        // Not played: a bye, a missed game, or one still to come. Not expandable -- there is
        // no stat row, so there is nothing to break down.
        if (w.kind !== 'played') {
          return (
            <div key={w.key} ref={ref} data-kind={w.kind}
                 className={cn('flex items-center gap-2 sm:gap-3 px-4 py-2.5', isCurrent && 'bg-muted/10')}>
              {weekCell}
              {w.kind === 'dnp' && (
                // Distinct from a future row on purpose: dashes alone would not say whether
                // the game happened.
                <span className="shrink-0 rounded bg-muted px-1 text-[10px] font-bold text-muted-foreground" title="Did not play">DNP</span>
              )}
              {w.kind === 'upcoming' && isCurrent && currentWeekProj !== undefined && (
                <span className="shrink-0 text-[10px] text-muted-foreground" data-testid="gamelog-proj">
                  Proj {formatWeeklyProj(currentWeekProj)}
                </span>
              )}
              <div className="flex-1 flex items-center gap-2 sm:gap-3 justify-end">
                {w.kind !== 'bye' && (
                  <>
                    <div className="text-right whitespace-nowrap"><span className="text-[10px] text-muted-foreground">TRS </span>{dash}</div>
                    <div className="text-right whitespace-nowrap"><span className="text-[10px] text-muted-foreground">ANT </span>{dash}</div>
                    <div className="text-right w-14 sm:w-16">{dash}</div>
                  </>
                )}
                {/* Chevron-width spacer so the columns line up with the played rows. */}
                <span className="w-3.5 shrink-0" />
              </div>
            </div>
          );
        }

        const g = w.stat;
        const isExpanded = expandedKey === w.key;
        const isHot = hotKeys.has(w.key);
        const sabPts = Number(g.sabotage_points) || 0;
        const trsPts = Number(g.trash_points) || 0;
        const antPts = Number(g.anti_points) || 0;

        return (
          <div key={w.key} ref={ref} data-kind="played" className={cn(isCurrent && 'bg-muted/10')}>
            <button
              onClick={() => onToggle(w.key)}
              className="w-full flex items-center gap-2 sm:gap-3 px-4 py-2.5 hover:bg-muted/20 transition-colors text-left"
            >
              {weekCell}

              {/* Hot streak */}
              {isHot && (
                <span className="text-xs shrink-0" title="Hot streak">🔥</span>
              )}

              {/* Scores */}
              <div className="flex-1 flex items-center gap-2 sm:gap-3 justify-end">
                <div className="text-right whitespace-nowrap">
                  <span className="text-[10px] text-muted-foreground">TRS </span>
                  <span className="text-xs font-bold text-damage tabular-nums">+{trsPts.toFixed(1)}</span>
                </div>
                <div className="text-right whitespace-nowrap">
                  <span className="text-[10px] text-muted-foreground">ANT </span>
                  <span className="text-xs font-bold text-emerald-400 tabular-nums">{antPts.toFixed(1)}</span>
                </div>
                <div className="text-right w-14 sm:w-16">
                  <span className={cn(
                    'text-sm font-bold tabular-nums',
                    sabPts > 0 ? 'text-primary' : sabPts < 0 ? 'text-emerald-400' : 'text-muted-foreground'
                  )}>
                    {sabPts >= 0 ? '+' : ''}{sabPts.toFixed(1)}
                  </span>
                </div>
                {isExpanded ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground shrink-0" /> : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
              </div>
            </button>

            {/* Expanded detail.
                PREFERRED: what actually scored, from the model weights (SAB-1123). This is the
                only view that can show a term the hand-kept POSITION_STATS list omits -- e.g. a
                run stuffed for loss (rushing.tlost / tlost_yards), which moves the score and had
                no display at all. It is also self-limiting in the other direction: a stat with no
                weight yields no contribution, so punt yards/avg and COACH wins/losses can no
                longer sit beside a score implying they contributed.

                FALLBACK: raw counting stats. computeBreakdown BLOCKS whenever the detail cannot be
                trusted, and its contract forbids rendering contributions anyway. That is not a
                rare branch: team_lost/position are NULL on every row before SAB-825, so 2024 and
                2025 block entirely and only 66 of 38,938 rows currently reconcile. Showing the raw
                line is honest there; an empty panel would not be. */}
            {isExpanded && (() => {
              const bd: BreakdownResult | null = terms
                ? computeBreakdown(
                    {
                      stats: (g.stats as Record<string, unknown>) ?? null,
                      team_lost: g.team_lost ?? null,
                      position: g.position ?? position ?? null,
                      outcome_final: g.outcome_final ?? null, // SAB-1127
                      storedTrash: Number(g.trash_points) || 0,
                      storedAnti: Number(g.anti_points) || 0,
                    },
                    terms,
                  )
                : null;
              const contribs = bd && bd.blocked === null ? bd.breakdown!.contributions : null;
              const pos = contribs?.filter(c => c.pts > 0) ?? [];
              const neg = contribs?.filter(c => c.pts < 0) ?? [];

              // SAB-1176: the arithmetic column. `flat` reads "22 × +1"; a rate carries its
              // divisor so "143 × -3/100" cannot be mistaken for -3 per yard, which is the
              // trap SAB-1165 hit for real (a -2 override became -2.00 PER YARD and a
              // 30-yard TD cost -60 against a designed -4).
              const arithmetic = (c: { raw: number; points: number; unit: string }) => {
                const w = c.points >= 0 ? `+${c.points}` : `${c.points}`;
                const per: Record<string, string> = {
                  per_100_yards: '/100', per_10_yards: '/10', per_full_10: ' per full 10',
                  per_7_band: ' per 7 (max 3)',
                };
                return `${c.raw} × ${w}${per[c.unit] ?? ''}`;
              };

              return (
                <div className="space-y-3 px-4 pb-4 pt-1">
                  {/* ── STAT LINE ── always shown: it is the raw truth, and it is the only
                      thing left to read when the breakdown is blocked. */}
                  <div>
                    <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                      Stat line
                    </p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1">
                      {statKeys.map(({ key, label, path }) => {
                        const val = statAt(g.stats, path);
                        return (
                          <span key={key} className="inline-flex items-baseline gap-1.5">
                            <span className="text-[11px] text-muted-foreground">{label}</span>
                            <span className="text-xs font-semibold tabular-nums text-foreground">
                              {Number.isInteger(val) ? val : val.toFixed(1)}
                            </span>
                          </span>
                        );
                      })}
                    </div>
                  </div>

                  {/* ── HOW IT SCORED ──
                      Sabotage scoring is unintuitive -- a QB's 22 pass attempts are worth +22
                      while his 143 passing yards are -4.3 -- so this table is the only thing
                      that makes the number trustworthy.

                      IT RENDERS ONLY WHEN IT RECONCILES, AND IS ABSENT OTHERWISE. No header,
                      no message, no explanation. computeBreakdown blocks whenever the detail
                      cannot be trusted, and that is the common case -- every row before
                      SAB-825 lacks the scoring context, and an override league mismatches by
                      construction (the stored score in nfl_player_game_stats carries no league
                      overrides, while these terms do).

                      A user does not need telling that a breakdown exists and is missing; that
                      explains our internal reconciliation check to someone who never asked
                      about it. An absent section is quieter and more honest. The stat line and
                      the week score above still say everything we can stand behind.

                      THE CONTRACT IS UNCHANGED: contributions render only when
                      blocked === null. This removes the message, not the guard. */}
                  {contribs && contribs.length > 0 && (
                    <div>
                      <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                        How it scored
                      </p>
                      <div className="overflow-hidden rounded-[10px] border border-secondary bg-background">
                        {contribs.map(c => (
                          <div key={`${c.source}-${c.event}`}
                               className="flex items-center gap-2 border-b border-secondary/60 px-2.5 py-1.5 last:border-b-0">
                            <span className="min-w-0 flex-1 truncate text-[11px] text-foreground">{c.event}</span>
                            <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
                              {arithmetic(c)}
                            </span>
                            <span className={cn(
                              'w-14 shrink-0 text-right text-xs font-bold tabular-nums',
                              c.pts >= 0 ? 'text-damage' : 'text-success',
                            )}>
                              {c.pts >= 0 ? '+' : ''}{c.pts.toFixed(2)}
                            </span>
                          </div>
                        ))}
                        <div className="flex items-center gap-2 bg-card px-2.5 py-1.5">
                          <span className="flex-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                            Week total
                          </span>
                          <span className="w-14 shrink-0 text-right text-xs font-bold tabular-nums text-primary">
                            {sabPts >= 0 ? '+' : ''}{sabPts.toFixed(1)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Injury Tab                                                         */
/* ------------------------------------------------------------------ */

function InjuryTab({ player }: { player: PlayerInfo }) {
  const status = player.injury_status;
  const isHealthy = !status || status === 'Active' || status === 'Healthy';
  const isIReligible = status ? IR_ELIGIBLE.includes(status) : false;

  return (
    <div className="p-4 space-y-4">
      {isHealthy ? (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <CheckCircle className="h-10 w-10 text-emerald-400 mb-3" />
          <p className="text-sm font-medium text-emerald-400">No Current Injuries</p>
          <p className="text-xs text-muted-foreground mt-1">Player is active and healthy</p>
        </div>
      ) : (
        <>
          {/* Current status */}
          <div className="bg-muted/20 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Current Status</span>
              {isIReligible && (
                <Badge variant="outline" className="text-[10px] border-red-800/30 text-red-300">
                  IR Eligible
                </Badge>
              )}
            </div>
            <Badge className={cn(
              'text-sm font-bold border px-3 py-1',
              injuryColors[status!] || 'text-red-400 bg-red-400/10 border-red-400/20'
            )}>
              {status}
            </Badge>
            {player.injury_body_part && (
              <p className="text-sm text-foreground mt-2">
                <span className="text-muted-foreground">Injury: </span>
                {player.injury_body_part}
              </p>
            )}
          </div>

          {/* IR eligibility note */}
          <div className="text-xs text-muted-foreground bg-muted/10 rounded-lg p-3">
            <p className="font-medium text-foreground mb-1">IR Slot Eligibility</p>
            <p>Players with Out, IR, Doubtful, Suspended, or PUP designations can be placed in an IR slot.</p>
            <p className="mt-1">Questionable and Probable players are <span className="text-amber-400">not</span> IR-eligible.</p>
          </div>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Activity Tab                                                       */
/* ------------------------------------------------------------------ */

function ActivityTab({ transactions }: { transactions: TransactionRow[] }) {
  if (transactions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Activity className="h-8 w-8 text-muted-foreground mb-3" />
        <p className="text-sm text-muted-foreground">No transaction history</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-border/50">
      {transactions.map(tx => {
        const date = new Date(tx.created_at);
        const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

        let description = '';
        let color = 'text-muted-foreground';
        switch (tx.transaction_type) {
          case 'add':
            description = `Added by ${tx.team_name}`;
            color = 'text-emerald-400';
            break;
          case 'drop':
            description = `Dropped by ${tx.team_name}`;
            color = 'text-red-400';
            break;
          case 'trade':
            description = `Traded from ${tx.from_team_name} to ${tx.to_team_name}`;
            color = 'text-sky-400';
            break;
          case 'waiver':
            description = `Claimed by ${tx.team_name}`;
            color = 'text-amber-400';
            break;
          case 'draft':
            description = `Drafted by ${tx.team_name}`;
            color = 'text-purple-400';
            break;
          default:
            description = `${tx.transaction_type}: ${tx.team_name}`;
        }

        return (
          <div key={tx.id} className="flex items-center gap-3 px-4 py-3">
            <div className="flex-1 min-w-0">
              <p className={cn('text-sm font-medium', color)}>{description}</p>
            </div>
            <span className="text-[10px] text-muted-foreground shrink-0">{dateStr}</span>
          </div>
        );
      })}
    </div>
  );
}
